<script setup lang="ts">
import type { TraderMarketInfluenceItem } from '~/server/detail'

const props = withDefaults(
  defineProps<{
    show: boolean
    influenceRating?: string
    marketInfluenceCountries?: TraderMarketInfluenceItem[] | null
  }>(),
  {
    influenceRating: '',
    marketInfluenceCountries: () => [],
  },
)

const emit = defineEmits<{
  close: []
}>()

const { lock: lockBackgroundScroll, unlock: unlockBackgroundScroll } = useStickyDialogScrollLock()

const close = () => {
  emit('close')
}

watch(
  () => props.show,
  (value) => {
    if (!import.meta.client) return

    if (value) {
      lockBackgroundScroll()
      return
    }

    unlockBackgroundScroll()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  unlockBackgroundScroll()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="influence-dialog-fade">
      <section v-if="props.show" class="influence-dialog-mask" tabindex="-1" @keydown.esc="close">
        <div class="influence-dialog" role="dialog" aria-modal="true" aria-label="展业区域影响力弹窗">
          <button type="button" class="influence-dialog-close" aria-label="关闭影响力弹窗" @click="close">
            <CommonImage
              class="size-[32px]"
              src="https://resources1.interface003.com/web30/2026/07/02/e1866379-c778-4f76-9890-f2e3eb5820b8.png"
              alt=""
            />
          </button>

          <div class="influence-dialog-body">
            <ExchangeDetailBusinessAreaCard
              :influence-rating="props.influenceRating"
              :market-influence-countries="props.marketInfluenceCountries"
            />
          </div>
        </div>
      </section>
    </Transition>
  </Teleport>
</template>

<style scoped lang="less">
.influence-dialog-mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(9, 20, 51, 0.48);
  padding: 32px;
}

.influence-dialog {
  position: relative;
  width: min(760px, calc(100vw - 48px));
  max-height: calc(100vh - 64px);
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(8, 19, 62, 0.18);
  color: #292a2e;
  font-family:
    Outfit,
    PingFang SC,
    sans-serif;
}

.influence-dialog-close {
  position: absolute;
  top: 32px;
  right: 32px;
  z-index: 2;
  display: flex;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: #eef0f4;
  transition:
    background 0.2s ease,
    transform 0.2s ease;
}

.influence-dialog-body {
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  background: #fff;
  padding: 32px;
}

.influence-dialog-body :deep(.exchange-business-card) {
  box-shadow: none;
  padding: 0;
}

.influence-dialog-body :deep(.business-card-header) {
  padding-right: 48px;
}

.influence-dialog-body :deep(.business-card-body) {
  height: 302px;
  align-items: stretch;
  gap: 20px;
}

.influence-dialog-body :deep(.map-interactive-box) {
  height: 302px;
  border-radius: 0;
}

.influence-dialog-body :deep(.business-map) {
  left: 0;
  width: 100%;
  height: 268px;
}

.influence-dialog-body :deep(.legend-item) {
  width: 50px;
}

.influence-dialog-body :deep(.map-zoom-control) {
  right: 0;
}

.influence-dialog-body :deep(.country-score-panel) {
  width: 254px;
  height: 302px;
  padding: 12px 12px 0;
}

.influence-dialog-body :deep(.country-score-header) {
  padding-bottom: 8px;
}

.influence-dialog-body :deep(.country-score-row) {
  height: 44px;
  padding: 8px;
}

.influence-dialog-body :deep(.country-score-row:hover),
.influence-dialog-body :deep(.country-score-row:focus-visible),
.influence-dialog-body :deep(.country-score-row.row-highlight) {
  height: 44px;
  gap: 8px;
  padding: 8px;

  .country-name {
    flex: 1;
    font-size: 14px;
    line-height: 22px;
  }
}

.influence-dialog-fade-enter-active,
.influence-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.influence-dialog-fade-enter-active .influence-dialog,
.influence-dialog-fade-leave-active .influence-dialog {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.influence-dialog-fade-enter-from,
.influence-dialog-fade-leave-to {
  opacity: 0;
}

.influence-dialog-fade-enter-from .influence-dialog,
.influence-dialog-fade-leave-to .influence-dialog {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@media screen and (max-width: 799px) {
  .influence-dialog-mask {
    padding: 16px;
  }

  .influence-dialog {
    width: 100%;
    border-radius: 20px;
  }

  .influence-dialog-close {
    top: 24px;
    right: 24px;
  }

  .influence-dialog-body {
    padding: 24px;
  }

  .influence-dialog-body :deep(.business-card-header) {
    padding-right: 40px;
  }

  .influence-dialog-body :deep(.business-card-body) {
    height: auto;
  }

  .influence-dialog-body :deep(.map-interactive-box) {
    height: 320px;
  }

  .influence-dialog-body :deep(.business-map) {
    height: 284px;
  }

  .influence-dialog-body :deep(.country-score-panel) {
    width: 100%;
    height: 320px;
  }
}
</style>
